import React from 'react';
import { Grid, Typography, Box, Card, CardContent, Button, makeStyles } from '@material-ui/core';
import { Header, Page, Content } from '@backstage/core-components';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DashboardIcon from '@material-ui/icons/Dashboard';
import NotificationsIcon from '@material-ui/icons/Notifications';
import ExploreIcon from '@material-ui/icons/Explore';
import SettingsIcon from '@material-ui/icons/Settings';
import PeopleIcon from '@material-ui/icons/People';
import StorageIcon from '@material-ui/icons/Storage';
import TvIcon from '@material-ui/icons/Tv';
import { Link } from '@backstage/core-components';

const useStyles = makeStyles((theme) => ({
  quickAccessCard: {
    minHeight: '180px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  iconContainer: {
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  liveViewCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    marginBottom: theme.spacing(3),
  },
}));

const quickAccessItems = [
  {
    title: 'Browse All Dashboards',
    description: 'View and manage all available dashboards',
    icon: <DashboardIcon style={{ fontSize: '48px', color: '#667eea' }} />,
    url: '/dashboards',
  },
  {
    title: 'View Alerts',
    description: 'Monitor active alerts and notifications',
    icon: <NotificationsIcon style={{ fontSize: '48px', color: '#f093fb' }} />,
    url: '/alerting/list',
  },
  {
    title: 'Explore Data',
    description: 'Query and explore your data sources',
    icon: <ExploreIcon style={{ fontSize: '48px', color: '#4facfe' }} />,
    url: '/explore',
  },
  {
    title: 'Data Sources',
    description: 'Manage and configure data sources',
    icon: <StorageIcon style={{ fontSize: '48px', color: '#43e97b' }} />,
    url: '/datasources',
  },
  {
    title: 'Users & Teams',
    description: 'Manage users and team permissions',
    icon: <PeopleIcon style={{ fontSize: '48px', color: '#fa709a' }} />,
    url: '/org/users',
  },
  {
    title: 'Configuration',
    description: 'Access Grafana settings and preferences',
    icon: <SettingsIcon style={{ fontSize: '48px', color: '#ffecd2' }} />,
    url: '/admin',
  },
];

export const GrafanaPage = () => {
  const classes = useStyles();
  const grafanaUrl = 'https://g-8e977d52f3.grafana-workspace.us-east-1.amazonaws.com';

  return (
    <Page themeId="tool">
      <Header 
        title="Grafana Dashboards" 
        subtitle="AWS Managed Grafana - Quick Access Portal"
      />
      <Content>
        <Card className={classes.liveViewCard}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <TvIcon style={{ fontSize: '48px', marginRight: '16px' }} />
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Grafana Access
                  </Typography>
                  <Typography variant="body1">
                    AWS Managed Grafana may not support iframe embedding. Use direct access for full functionality.
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<OpenInNewIcon />}
                  href={grafanaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Grafana Directly
                </Button>

                <Box ml={2}>
                  <Link to="/grafana-live">
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="large"
                      startIcon={<TvIcon />}
                    >
                      Try Embedded View
                    </Button>
                  </Link>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {quickAccessItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Card 
                className={classes.quickAccessCard}
                onClick={() => window.open(`${grafanaUrl}${item.url}`, '_blank')}
              >
                <CardContent>
                  <div className={classes.iconContainer}>
                    {item.icon}
                  </div>
                  <Typography variant="h6" gutterBottom align="center">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center" paragraph>
                    {item.description}
                  </Typography>
                  <div className={classes.buttonContainer}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<OpenInNewIcon />}
                      href={`${grafanaUrl}${item.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Content>
    </Page>
  );
};